/**
 * 逐字稿分段的纯函数——时间戳格式化与检索过滤。
 *
 * 独立成纯函数以便脱离组件直接验证：检索要同时做「过滤」与「高亮命中片段」
 * 两件事，写在组件里不便单独测试匹配逻辑本身是否正确。
 */

import type { MeetingTranscriptSegment } from "../types/meeting";

/** 毫秒时间戳格式化为 mm:ss（超过一小时也只进位到分钟，逐字稿场景足够）。 */
export function formatSegmentTimestamp(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** 一次检索匹配：命中的分段 + 该分段文本里各次命中的起止下标（供高亮）。 */
export interface SegmentSearchMatch {
  segment: MeetingTranscriptSegment;
  ranges: [number, number][];
}

/**
 * 按检索词过滤分段并给出高亮区间。空检索词返回全部分段、无高亮区间。
 * 大小写不敏感；纯子串匹配，不支持正则，避免用户输入被误当成正则语法。
 */
export function matchTranscriptSegments(
  segments: MeetingTranscriptSegment[],
  query: string,
): SegmentSearchMatch[] {
  const q = query.trim().toLowerCase();
  if (!q) return segments.map((segment) => ({ segment, ranges: [] }));

  const matches: SegmentSearchMatch[] = [];
  for (const segment of segments) {
    const lower = segment.text.toLowerCase();
    const ranges: [number, number][] = [];
    let from = 0;
    for (;;) {
      const at = lower.indexOf(q, from);
      if (at === -1) break;
      ranges.push([at, at + q.length]);
      from = at + q.length;
    }
    if (ranges.length > 0) matches.push({ segment, ranges });
  }
  return matches;
}
